import React, { useMemo } from "react";

interface Props {
  count?: number;          // number of drops, default 10
  color?: string;          // base drop colour (rgba), default semi-transparent white-blue
  minDur?: number;         // min fall duration seconds, default 1.6
  maxDur?: number;         // max fall duration seconds, default 3.2
  minSize?: number;        // min drop diameter px, default 4
  maxSize?: number;        // max drop diameter px, default 9
  splashIntensity?: number; // 0-1, controls secondary droplet count/size, default 0.6
}

interface Drop {
  id: number;
  left: number;       // % from left
  size: number;        // px diameter
  dur: number;          // fall animation duration seconds
  delay: number;        // start delay seconds
  drift: number;        // px horizontal sway during fall
  splashDur: number;    // ripple duration seconds
  ring2Delay: number;   // second ripple ring stagger
  particles: { angle: number; dist: number; size: number; delay: number }[];
}

/**
 * Droplets — realistic falling water drops with specular highlight,
 * impact squash, layered elliptical ripples, and secondary splash
 * particles. Zero assets, pure CSS keyframes.
 */
const Droplets: React.FC<Props> = ({
  count = 10,
  color = "rgba(180, 220, 255, 0.55)",
  minDur = 1.6,
  maxDur = 3.2,
  minSize = 4,
  maxSize = 9,
  splashIntensity = 0.6,
}) => {
  const drops = useMemo<Drop[]>(() => {
    const result: Drop[] = [];
    for (let i = 0; i < count; i++) {
      const r = (n: number) => {
        const x = Math.sin(i * 9301 + n * 49297 + 233) * 10000;
        return x - Math.floor(x);
      };
      const size = minSize + r(1) * (maxSize - minSize);
      const particleCount = Math.round(2 + splashIntensity * 3);
      const particles = Array.from({ length: particleCount }, (_, p) => ({
        angle: (360 / particleCount) * p + r(10 + p) * 40 - 20,
        dist: (size * 1.8 + r(20 + p) * size * 1.2) * splashIntensity,
        size: 1 + r(30 + p) * 1.5,
        delay: r(40 + p) * 0.05,
      }));

      result.push({
        id: i,
        left: 5 + r(0) * 90,
        size,
        dur: minDur + r(2) * (maxDur - minDur),
        delay: r(3) * 4,
        drift: (r(5) - 0.5) * 14,
        splashDur: 0.55 + r(4) * 0.35,
        ring2Delay: 0.08 + r(6) * 0.1,
        particles,
      });
    }
    return result;
  }, [count, minDur, maxDur, minSize, maxSize, splashIntensity]);

  return (
    <>
      <style>{`
        @keyframes oot-drop-fall {
          0%    { transform: translate(0, -10px) scale(1, 1); opacity: 0; }
          6%    { opacity: 1; }
          82%   { transform: translate(var(--drift), 88vh) scale(1, 1); opacity: 1; }
          90%   { transform: translate(var(--drift), 97vh) scale(1.35, 0.55); opacity: 0.9; }
          100%  { transform: translate(var(--drift), 101vh) scale(1.6, 0.25); opacity: 0; }
        }
        @keyframes oot-drop-streak {
          0%    { opacity: 0; transform: scaleY(0.6); }
          8%    { opacity: 0.5; }
          80%   { opacity: 0.35; }
          92%   { opacity: 0; transform: scaleY(1.4); }
          100%  { opacity: 0; }
        }
        @keyframes oot-ring {
          0%   { transform: translateX(-50%) scale(0.2, 0.12); opacity: 0.75; border-width: 1.6px; }
          60%  { opacity: 0.35; }
          100% { transform: translateX(-50%) scale(2.6, 1.0); opacity: 0; border-width: 0.4px; }
        }
        @keyframes oot-particle {
          0%   { transform: translate(-50%, -50%) translate(0, 0) scale(1); opacity: 0.9; }
          40%  { transform: translate(-50%, -50%) translate(var(--px), var(--py)) scale(0.9); opacity: 0.7; }
          100% { transform: translate(-50%, -50%) translate(calc(var(--px) * 1.15), calc(var(--py) + 6px)) scale(0.2); opacity: 0; }
        }
      `}</style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {drops.map((d) => {
          const landTime = d.delay + d.dur * 0.88;
          return (
            <div key={d.id} style={{ position: "absolute", left: `${d.left}%`, top: 0, width: 0, height: 0 }}>
              {/* Falling drop with highlight, drift and impact squash */}
              <div
                style={
                  {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: d.size,
                    height: d.size * 1.5,
                    borderRadius: "50% 50% 50% 50% / 62% 62% 38% 38%",
                    background: `radial-gradient(circle at 30% 25%, rgba(255,255,255,0.9) 0%, ${color} 45%, ${color} 100%)`,
                    boxShadow: `0 0 ${d.size * 0.4}px rgba(255,255,255,0.15)`,
                    animation: `oot-drop-fall ${d.dur}s cubic-bezier(0.55,0.06,0.68,0.19) ${d.delay}s infinite`,
                    "--drift": `${d.drift}px`,
                  } as React.CSSProperties
                }
              />
              {/* Faint motion-blur streak trailing the drop */}
              <div
                style={
                  {
                    position: "absolute",
                    top: 0,
                    left: d.size * 0.15,
                    width: Math.max(1.2, d.size * 0.22),
                    height: d.size * 3,
                    background: `linear-gradient(to bottom, transparent, ${color})`,
                    borderRadius: "2px",
                    filter: "blur(0.5px)",
                    animation: `oot-drop-streak ${d.dur}s cubic-bezier(0.55,0.06,0.68,0.19) ${d.delay}s infinite`,
                    "--drift": `${d.drift}px`,
                  } as React.CSSProperties
                }
              />
              {/* Ripple ring 1 */}
              <div
                style={{
                  position: "absolute",
                  top: "88vh",
                  left: "50%",
                  width: d.size * 3.2,
                  height: d.size * 1.1,
                  borderRadius: "50%",
                  border: `1.5px solid ${color}`,
                  animation: `oot-ring ${d.splashDur}s ease-out ${landTime}s infinite`,
                }}
              />
              {/* Ripple ring 2 (staggered, slightly larger, reinforces layered wave look) */}
              <div
                style={{
                  position: "absolute",
                  top: "88vh",
                  left: "50%",
                  width: d.size * 2.6,
                  height: d.size * 0.9,
                  borderRadius: "50%",
                  border: `1px solid ${color}`,
                  animation: `oot-ring ${d.splashDur * 1.15}s ease-out ${landTime + d.ring2Delay}s infinite`,
                }}
              />
              {/* Secondary splash particles kicked up on impact */}
              {d.particles.map((p, idx) => {
                const rad = (p.angle * Math.PI) / 180;
                const px = Math.cos(rad) * p.dist;
                const py = -Math.abs(Math.sin(rad) * p.dist * 0.8) - p.dist * 0.3;
                return (
                  <div
                    key={idx}
                    style={
                      {
                        position: "absolute",
                        top: "88vh",
                        left: "50%",
                        width: p.size,
                        height: p.size,
                        borderRadius: "50%",
                        background: color,
                        animation: `oot-particle ${d.splashDur * 0.9}s ease-out ${landTime + p.delay}s infinite`,
                        "--px": `${px}px`,
                        "--py": `${py}px`,
                      } as React.CSSProperties
                    }
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Droplets;